import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './PaymentTermForm.css';
import '../../../shared/StyledInput.css';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import ToggleSwitch from '../../../shared/ToggleSwitch';
import FieldHelp from '../../../shared/FieldHelp';

function PaymentTermForm({ initial, onSave, onCancel, loading }) {
  const [name, setName] = useState(initial?.name || '');
  const [days, setDays] = useState(initial?.days ?? '');
  const [description, setDescription] = useState(initial?.description || '');
  const [isPrepay, setIsPrepay] = useState(!!initial?.is_prepay);
  const [quoteEmailTemplateId, setQuoteEmailTemplateId] = useState(initial?.quote_email_template_id ? String(initial.quote_email_template_id) : '');
  const [quoteTemplates, setQuoteTemplates] = useState([]);
  const [error, setError] = useState('');

  // Active templates used for Quotes. A picked template since deactivated
  // or no longer used for Quotes drops back to Automatic.
  useEffect(() => {
    axios.get('/api/email-templates', { params: { status: 'active' } })
      .then((res) => {
        const rows = (res.data.data || []).filter((t) => t.for_quotes);
        setQuoteTemplates(rows);
        setQuoteEmailTemplateId((prev) => (rows.some((t) => String(t.id) === prev) ? prev : ''));
      })
      .catch(() => setQuoteTemplates([]));
  }, []);

  const validate = () => {
    if (!name.trim()) return 'Name is required.';
    if (days === '' || days === null) return 'Days is required.';
    const daysNum = Number(days);
    if (!Number.isInteger(daysNum) || daysNum < 0) return 'Days must be a non-negative whole number.';
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setError(err);
    setError('');
    onSave({
      name: name.trim(),
      days: Number(days),
      description: description.trim(),
      is_prepay: isPrepay,
      quote_email_template_id: quoteEmailTemplateId || null,
    });
  };

  return (
    <form className="payment-term-form" onSubmit={handleSubmit} style={{ minWidth: 320 }}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <div>
          <label>Name<RequiredMark /></label><br />
          <input
            className="styled-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            placeholder="e.g., Net 30"
          />
        </div>
        <div>
          <label>Days<RequiredMark /></label><br />
          <input
            className="styled-input"
            type="number"
            min={0}
            step={1}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            placeholder="e.g., 30"
            style={{ width: 100 }}
          />
        </div>
        <div style={{ flexBasis: '100%' }}>
          <label>Description</label><br />
          <input
            className="styled-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 6 }}>
          <ToggleSwitch checked={isPrepay} onChange={setIsPrepay} label="Prepay" />
          <FieldHelp
            fieldKey="sales-payment-terms.prepay"
            pageKey="sales-payment-terms"
            label="Prepay"
          >
            For Customers who pay before anything ships. Their Quotes and Sales Orders show each option that's on in Receivables › Payment Options.
          </FieldHelp>
        </div>
        <div style={{ flexBasis: '100%' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            Quote Email Template
            <FieldHelp
              fieldKey="sales-payment-terms.quote-email-template"
              pageKey="sales-payment-terms"
              label="Quote Email Template"
            >
              The email a Quote goes out with when the Customer is on this term. Automatic uses the Quote – Prepay template for a Prepay term, and the Quote template otherwise. Any Email Template marked Used for Quotes can be picked.
            </FieldHelp>
          </label><br />
          <select
            className="styled-input"
            value={quoteEmailTemplateId}
            onChange={(e) => setQuoteEmailTemplateId(e.target.value)}
            style={{ width: '100%' }}
          >
            <option value="">Automatic</option>
            {quoteTemplates.map((t) => (
              <option key={t.id} value={String(t.id)}>{t.name}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}

      <div style={{ marginTop: 12 }}>
        <SaveButton loading={loading} />
        <CancelButton onClick={onCancel} disabled={loading} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

export default PaymentTermForm;
